import type { SnowflakeSettings, SnowflakeTest, SnowflakeView } from '../types/snowflake'

// Display helpers for the Snowflake connector page. No React, no fetch, no decisions the API has not already made:
// these only turn the view and a test result into the words the page shows.

export type SnowflakeStatus = 'not-set-up' | 'waiting' | 'connected'

/** Where the connection stands: nothing saved, saved but Snowflake has not accepted the key, or working. */
export function statusOf(view: Pick<SnowflakeView, 'configured' | 'verified'>): SnowflakeStatus {
  if (!view.configured) return 'not-set-up'
  return view.verified ? 'connected' : 'waiting'
}

/** The short label for the status tag. */
export function statusLabel(status: SnowflakeStatus): string {
  return status === 'connected' ? 'Connected' : status === 'waiting' ? 'Waiting on Snowflake' : 'Not set up'
}

/** A test passed when Snowflake signed the service user in. A hint may still ride along: a grant is missing. */
export function testPassed(test: Pick<SnowflakeTest, 'reachable' | 'authenticated'>): boolean {
  return test.reachable && test.authenticated
}

/** One test as a sentence: who Snowflake says Scout is on a pass, Snowflake's own words on a failure. */
export function testSentence(test: SnowflakeTest): string {
  if (!testPassed(test)) {
    if (test.error) return test.error
    return test.reachable ? 'Snowflake refused the sign-in.' : 'Nothing answered at the Snowflake address.'
  }
  const parts = [
    `Snowflake answered in ${Math.round(test.latencyMs).toLocaleString('en-US')} ms as ${test.asUser ?? 'an unnamed user'}`,
    test.asRole ? `role ${test.asRole}` : 'no role',
    test.warehouse ? `warehouse ${test.warehouse}` : 'no warehouse',
    test.database ? `database ${test.database}` : 'no database',
    test.schema ? `schema ${test.schema}` : 'no schema',
  ]
  return parts.join(', ')
}

/** The six names in the order the form asks for them, with the one line that says what each is for. */
export const SNOWFLAKE_FIELDS: { key: keyof SnowflakeSettings; label: string; hint: string; placeholder?: string }[] = [
  { key: 'account', label: 'Account', placeholder: 'MYORG-MYACCOUNT', hint: 'The part of your Snowflake address before .snowflakecomputing.com — MYORG-MYACCOUNT; a pasted URL is fine.' },
  { key: 'user', label: 'Service user', hint: 'A service user Scout signs in as; created by step 1 below if it does not exist.' },
  { key: 'role', label: 'Role', hint: 'The read-only role every statement runs under.' },
  { key: 'warehouse', label: 'Warehouse', hint: 'The warehouse Scout may use to run its reads.' },
  { key: 'database', label: 'Database', hint: 'The database Scout may read.' },
  { key: 'schema', label: 'Schema', hint: 'The schema inside that database whose tables and views Scout may read.' },
]

/** True when the form holds something the saved connection does not. */
export function settingsChanged(saved: SnowflakeSettings, draft: SnowflakeSettings): boolean {
  return SNOWFLAKE_FIELDS.some((f) => saved[f.key].trim() !== draft[f.key].trim())
}

/** Estimated rows as the table shows them: a grouped number, or a dash when Snowflake does not say. */
export function rowsLabel(estimatedRows: number | null): string {
  return estimatedRows == null ? '—' : estimatedRows.toLocaleString('en-US')
}
